<template>
  <div class="container-fluid mt-4">
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div
            class="card-header bg-white d-flex justify-content-between align-items-center py-3"
          >
            <h4 class="mb-0">Gestión de Roles</h4>
            <button class="btn btn-primary" @click="openCreateRoleModal">
              <i class="bi bi-plus-circle me-2"></i>Nuevo Rol
            </button>
          </div>
          <div class="card-body p-0">
            <ContentSkeleton
              v-if="isLoading"
              variant="table"
              :rows="5"
              :cols="5"
              label="Cargando roles..."
            />

            <div v-else-if="error" class="text-center py-5">
              <div class="text-danger mb-3">
                <i class="bi bi-exclamation-circle fs-1"></i>
              </div>
              <p class="text-danger">{{ error }}</p>
              <button class="btn btn-outline-primary" @click="fetchRoles">
                <i class="bi bi-arrow-clockwise me-2"></i>Reintentar
              </button>
            </div>

            <div v-else class="table-responsive">
              <table class="table table-hover mb-0">
                <thead class="bg-light">
                  <tr>
                    <th class="border-0">Nombre</th>
                    <th class="border-0">Descripción</th>
                    <th class="border-0">Permisos</th>
                    <th class="border-0">Usuarios</th>
                    <th class="border-0 text-end">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="role in roles" :key="role._id">
                    <td class="align-middle">
                      <span class="fw-medium">{{ role.name }}</span>
                    </td>
                    <td class="align-middle">{{ role.description }}</td>
                    <td class="align-middle">
                      <div class="d-flex flex-wrap gap-1">
                        <span
                          v-for="(permission, index) in role.permissions || []"
                          :key="index"
                          class="badge bg-primary bg-opacity-10 text-primary"
                        >
                          {{ formatPermission(permission) }}
                        </span>
                      </div>
                    </td>
                    <td class="align-middle">
                      <div v-if="usersOfRole(role).length" class="small">
                        <div
                          v-for="user in usersOfRole(role)"
                          :key="user._id"
                          class="text-nowrap"
                          :title="user.email || ''"
                        >
                          {{ user.name || user.email }}
                        </div>
                      </div>
                      <span v-else class="text-muted small">Sin usuarios</span>
                    </td>
                    <td class="align-middle text-end">
                      <div class="d-flex justify-content-end gap-2">
                        <button
                          class="btn btn-sm btn-outline-primary d-inline-flex align-items-center"
                          @click="openEditRoleModal(role)"
                          :disabled="role.name === 'admin'"
                          title="Editar rol"
                        >
                          <i class="bi bi-pencil me-1"></i>
                          Editar
                        </button>
                        <button
                          class="btn btn-sm btn-outline-secondary d-inline-flex align-items-center"
                          @click="openCopyRoleModal(role)"
                          :disabled="role.name === 'admin'"
                          title="Crear un rol nuevo con los mismos permisos"
                        >
                          <i class="far fa-copy me-1"></i>
                          Copiar
                        </button>
                        <button
                          class="btn btn-sm btn-outline-danger d-inline-flex align-items-center"
                          @click="deleteRole(role)"
                          :disabled="role.name === 'admin'"
                          title="Eliminar rol"
                        >
                          <i class="bi bi-trash me-1"></i>
                          Eliminar
                        </button>
                      </div>
                    </td>
                  </tr>
                  <tr v-if="roles.length === 0">
                    <td colspan="5" class="text-center py-4">
                      <div class="text-muted">
                        <i class="bi bi-inbox fs-4 d-block mb-2"></i>
                        No hay roles disponibles
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Role Form Modal -->
    <RoleFormModal
      v-if="showRoleModal"
      :show="showRoleModal"
      @update:show="showRoleModal = $event"
      :role="selectedRole"
      :copy-from="copyFromRole"
      @role-updated="handleRoleUpdated"
    />
  </div>
</template>

<script>
import { wencesApi } from "@/api/wencesApi";
import Swal from "sweetalert2";
import RoleFormModal from "@/components/roles/RoleFormModal.vue";
import ContentSkeleton from "@/components/common/ContentSkeleton.vue";

export default {
  name: "Roles",
  components: {
    ContentSkeleton,
    RoleFormModal,
  },
  data() {
    return {
      roles: [],
      /** Usuarios activos agrupados por nombre de rol (la API devuelve el rol por nombre). */
      usersByRoleName: {},
      showRoleModal: false,
      selectedRole: null,
      copyFromRole: null,
      isLoading: true,
      error: null,
    };
  },
  methods: {
    async fetchRoles() {
      this.isLoading = true;
      this.error = null;
      try {
        const [response] = await Promise.all([
          wencesApi.get("/api/roles"),
          this.fetchUsersByRole(),
        ]);
        this.roles = response.data.data || [];
      } catch (error) {
        console.error("Error fetching roles:", error);
        this.error = "Error al cargar los roles";
        Swal.fire({
          title: "Error",
          text: "Error al cargar los roles",
          icon: "error",
        });
      } finally {
        this.isLoading = false;
      }
    },
    async fetchUsersByRole() {
      try {
        const { data } = await wencesApi.get("/api/users/all");
        const users = Array.isArray(data) ? data : (data && data.data) || [];
        const byRole = {};
        for (const user of users) {
          const roleName = user && typeof user.role === "string" ? user.role : "";
          if (!roleName) continue;
          (byRole[roleName] = byRole[roleName] || []).push(user);
        }
        for (const list of Object.values(byRole)) {
          list.sort((a, b) =>
            String(a.name || a.email || "").localeCompare(String(b.name || b.email || ""))
          );
        }
        this.usersByRoleName = byRole;
      } catch (error) {
        // Sin usuarios la tabla de roles se sigue viendo; la columna queda vacía.
        this.usersByRoleName = {};
      }
    },
    usersOfRole(role) {
      return (role && this.usersByRoleName[role.name]) || [];
    },
    formatPermission(permission) {
      if (!permission) return "";

      const parts = permission.split(".");
      if (parts.length !== 2) return permission;

      const [module, action] = parts;
      return `${module.charAt(0).toUpperCase() + module.slice(1)} - ${
        action.charAt(0).toUpperCase() + action.slice(1)
      }`;
    },
    openCreateRoleModal() {
      this.selectedRole = null;
      this.copyFromRole = null;
      this.showRoleModal = true;
    },
    openCopyRoleModal(role) {
      this.selectedRole = null;
      this.copyFromRole = { ...role };
      this.showRoleModal = true;
    },
    openEditRoleModal(role) {
      console.log("Opening edit modal for role:", role);
      this.copyFromRole = null;
      this.selectedRole = { ...role };
      this.showRoleModal = true;
      console.log("Modal state:", {
        showRoleModal: this.showRoleModal,
        selectedRole: this.selectedRole,
      });
    },
    async deleteRole(role) {
      try {
        const result = await Swal.fire({
          title: "¿Estás seguro?",
          text: `¿Deseas eliminar el rol "${role.name}"?`,
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Sí, eliminar",
          cancelButtonText: "Cancelar",
          confirmButtonColor: "#dc3545",
        });

        if (result.isConfirmed) {
          await wencesApi.delete(`/api/roles/${role._id}`);
          await this.fetchRoles();
          Swal.fire({
            title: "¡Eliminado!",
            text: "El rol ha sido eliminado correctamente",
            icon: "success",
          });
        }
      } catch (error) {
        Swal.fire({
          title: "Error",
          text: "Error al eliminar el rol",
          icon: "error",
        });
      }
    },
    handleRoleUpdated(updatedRole) {
      console.log("Role updated:", updatedRole);
      const index = this.roles.findIndex((r) => r._id === updatedRole._id);
      if (index !== -1) {
        this.roles[index] = updatedRole;
      } else {
        this.roles.unshift(updatedRole);
      }
      this.showRoleModal = false;
      this.selectedRole = null;
      this.fetchRoles();
    },
  },
  mounted() {
    this.fetchRoles();
  },
};
</script>

<style scoped>
.badge {
  font-size: 0.75rem;
  padding: 0.35em 0.65em;
}

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.375rem 0.75rem;
  font-size: 0.875rem;
  line-height: 1.5;
  border-radius: 0.25rem;
  transition: all 0.15s ease-in-out;
}

.btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.btn i {
  font-size: 0.875rem;
}

.table > :not(caption) > * > * {
  padding: 1rem;
}

.card {
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  border: none;
}

.card-header {
  border-bottom: 1px solid rgba(0, 0, 0, 0.125);
}

.gap-2 {
  gap: 0.5rem !important;
}

.btn-outline-primary {
  color: #0d6efd;
  border-color: #0d6efd;
}

.btn-outline-primary:hover {
  color: #fff;
  background-color: #0d6efd;
  border-color: #0d6efd;
}

.btn-outline-danger {
  color: #dc3545;
  border-color: #dc3545;
}

.btn-outline-danger:hover {
  color: #fff;
  background-color: #dc3545;
  border-color: #dc3545;
}
</style>
